// Post-auth invite wall (HOP-33 enforcement).

import { useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { isAxiosError } from 'axios'
import { Grain } from '@/components/hs/Grain'
import { apiClient } from '@/lib/apiClient'
import { useGlobalState } from '@/stores/globalState'
import { clearInviteCode, markKnownAccount } from '@/lib/inviteCode'
import { beginLogout } from '@/lib/logoutLatch'
import { InviteTicket } from './SignUpPage'
import type { User } from '@/types'
import '@/styles/auth-pages.css'

export function InviteWall() {
  const { logout: auth0Logout, user: auth0User } = useAuth0()
  const setUser = useGlobalState((s) => s.setUser)
  const setInviteRequired = useGlobalState((s) => s.setInviteRequired)

  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const ready = code.length === 6

  const submit = async () => {
    if (!ready || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const firstName =
        (auth0User?.given_name as string | undefined) ||
        (typeof auth0User?.name === 'string' ? auth0User.name.split(' ')[0] : undefined)
      await apiClient.post('/api/users/sync', {
        inviteCode: code,
        ...(auth0User?.email ? { email: auth0User.email } : {}),
        ...(firstName ? { firstName } : {}),
      })
      clearInviteCode()
      markKnownAccount()
      const { data } = await apiClient.get<User>('/api/users/me')
      setUser({ ...data, createdAt: new Date(data.createdAt) })
      setInviteRequired(false)
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 403) {
        setError("That code isn't valid or has already been used.")
      } else {
        setError('Could not check that code right now. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const useDifferentAccount = () => {
    beginLogout()
    auth0Logout({ logoutParams: { returnTo: window.location.origin } })
  }

  return (
    <div className="hs-brand auth-page">
      <Grain opacity={0.06} />

      <div className="auth-page__topbar">
        <div className="auth-brand-lockup">
          <span className="auth-brand-mark" />
          <span className="auth-brand-name">Hopscotch</span>
        </div>
      </div>

      <div className="auth-page__centered">
        <div className="auth-page__center-card auth-page__center-card--invite">
          <div className="auth-eyebrow auth-page__mini-eyebrow">
            Invite only · for now
          </div>
          <h1 className="auth-heading auth-heading--invite">
            One last thing — your invite.
          </h1>
          <p className="auth-copy auth-copy--invite">
            You&rsquo;re signed in, but Hopscotch is invite-only for now. Enter the code you were
            sent and we&rsquo;ll finish setting up your account.
          </p>

          <InviteTicket code={code} setCode={(c) => { setCode(c); if (error) setError(null) }} />

          {error && <p className="auth-error auth-error--invite">{error}</p>}

          <button
            type="button"
            onClick={submit}
            disabled={!ready || submitting}
            className={[
              'hs-press',
              'auth-submit-button',
              'auth-submit-button--invite',
              !ready ? 'is-disabled' : '',
            ].filter(Boolean).join(' ')}
          >
            {submitting ? 'Checking...' : 'Finish setting up'}
          </button>

          <p className="auth-switch-copy auth-switch-copy--invite">
            No code?{' '}
            <button
              type="button"
              onClick={useDifferentAccount}
              className="auth-link-button"
            >
              Use a different account
            </button>
          </p>
        </div>
      </div>
    </div>
  )
}
